/**
 * Web Target Auth Settings (Phase 9)
 * Settings panel for auth profile update behavior and staleness detection
 * Used in WebTarget configuration
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faQuestion, faPlus, faBolt, faClock, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const WebTargetAuthSettings = ({ settings, onChange, isDarkMode }) => {
  const updateMode = settings?.mode || 'auto';
  const stalenessConfig = settings?.stalenessDetection || {
    enabled: true,
    autoDetectBehaviorChange: true,
    maxAgeHours: 0,
    warningAgeHours: 24,
    criticalAgeHours: 168
  };

  const handleModeChange = (mode) => {
    onChange({
      ...settings,
      mode
    });
  };

  const handleStalenessChange = (field, value) => {
    onChange({
      ...settings,
      stalenessDetection: {
        ...stalenessConfig,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Update Behavior Mode */}
      <div>
        <label className={`block text-sm font-medium mb-3 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-700'
        }`}>
          Profile Update Behavior
          <span className="ml-2 text-xs font-normal text-gray-500">
            What happens when a user logs in again
          </span>
        </label>

        <div className="space-y-2">
          {/* Auto mode */}
          <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            updateMode === 'auto'
              ? isDarkMode
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-blue-500 bg-blue-50'
              : isDarkMode
              ? 'border-gray-700 hover:border-gray-600'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="updateMode"
              value="auto"
              checked={updateMode === 'auto'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium">
                <FontAwesomeIcon icon={faBolt} className="text-blue-500" />
                <span>Automatically update (silent)</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                }`}>
                  RECOMMENDED
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Profile updates automatically with fresh credentials. Shows toast notification.
                Best for most workflows - seamless and transparent.
              </p>
            </div>
          </label>

          {/* Ask mode */}
          <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            updateMode === 'ask'
              ? isDarkMode
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-blue-500 bg-blue-50'
              : isDarkMode
              ? 'border-gray-700 hover:border-gray-600'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="updateMode"
              value="ask"
              checked={updateMode === 'ask'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium">
                <FontAwesomeIcon icon={faQuestion} className="text-yellow-500" />
                <span>Ask before updating</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Shows modal asking to update existing or create new profile.
                More control but requires manual interaction.
              </p>
            </div>
          </label>

          {/* Create new mode */}
          <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            updateMode === 'create_new'
              ? isDarkMode
                ? 'border-blue-500 bg-blue-900/20'
                : 'border-blue-500 bg-blue-50'
              : isDarkMode
              ? 'border-gray-700 hover:border-gray-600'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name="updateMode"
              value="create_new"
              checked={updateMode === 'create_new'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 font-medium">
                <FontAwesomeIcon icon={faPlus} className="text-green-500" />
                <span>Always create new profile</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Never updates existing profiles. Creates new profile for each login.
                Use if you want to keep historical login sessions.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Staleness Detection */}
      <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <label className={`block text-sm font-medium mb-3 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-700'
        }`}>
          <FontAwesomeIcon icon={faClock} className="mr-2" />
          Staleness Detection
        </label>

        {/* Enable staleness detection */}
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={stalenessConfig.enabled}
            onChange={(e) => handleStalenessChange('enabled', e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Enable staleness detection</span>
        </label>

        {stalenessConfig.enabled && (
          <div className="space-y-4 pl-6">
            {/* Auto-detect behavior change */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={stalenessConfig.autoDetectBehaviorChange}
                onChange={(e) => handleStalenessChange('autoDetectBehaviorChange', e.target.checked)}
                className="rounded mt-0.5"
              />
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  Auto-detect when sessions expire
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                  }`}>
                    SMART
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Detects when authenticated requests that returned 200 OK start returning 302/401/403.
                  Automatically marks profile as stale after 2 occurrences.
                </p>
              </div>
            </label>

            {/* Time-based staleness */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={stalenessConfig.maxAgeHours > 0}
                  onChange={(e) => handleStalenessChange('maxAgeHours', e.target.checked ? 168 : 0)}
                  className="rounded mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium">Time-based staleness</div>
                  <p className="text-xs text-gray-500 mt-1">
                    Mark profile as stale after a certain time period (optional)
                  </p>
                </div>
              </label>

              {stalenessConfig.maxAgeHours > 0 && (
                <div className="pl-6 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 w-32">Mark stale after:</label>
                    <input
                      type="number"
                      value={stalenessConfig.maxAgeHours}
                      onChange={(e) => handleStalenessChange('maxAgeHours', parseInt(e.target.value) || 0)}
                      className={`w-20 px-2 py-1 rounded border text-sm ${
                        isDarkMode
                          ? 'bg-gray-700 border-gray-600 text-gray-200'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      min="1"
                    />
                    <span className="text-sm text-gray-500">hours</span>
                  </div>
                </div>
              )}
            </div>

            {/* Warning and critical thresholds */}
            <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
              <div className="text-sm font-medium mb-2">Visual Indicators</div>
              <div className="space-y-1 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="text-green-500">⚡</span>
                  <span>Fresh: Updated within 1 hour</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-yellow-500">⚠️</span>
                  <span>Warning: Older than {stalenessConfig.warningAgeHours} hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-500">🔴</span>
                  <span>Critical: Older than {stalenessConfig.criticalAgeHours} hours</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className={`flex items-start gap-2 p-3 rounded text-xs ${
        isDarkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-800'
      }`}>
        <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" />
        <div>
          <strong>Note:</strong> Individual profiles can override these settings if needed.
          You can configure per-profile behavior in the profile editor.
        </div>
      </div>
    </div>
  );
};

export default WebTargetAuthSettings;
